import { WritingTextarea } from "../../writing/writing-field";
import { useEffect, useRef, useState } from "react";
import { getOrCreateAuthSession } from "../../api/auth-client";
import type { AuthSessionResponse } from "../../api/auth.types";
import { sendAiChat, synthesizeAiSpeech, type AiVoiceProfile } from "../../api/ai-client";
import { loadLesson, saveLesson, TeacherClientError } from "../../api/teacher-client";
import { getActiveUltraOrganizationId } from "../../api/ultra-client";
import type { OmnisyniaClientModule } from "../module.types";
import { appendDictation, isAdvanceCommand, parseTeacherReply, teacherPrompt,
  type LessonState, type LessonPage } from "./teacher-state";
import "./teacher.css";

interface Recognition {
  lang: string; continuous: boolean; interimResults: boolean;
  onresult: ((event: { resultIndex: number; results: {
    length: number; [key: number]: { isFinal: boolean; [key: number]: { transcript: string } };
  } }) => void) | null;
  onerror: ((event: { error: string }) => void) | null;
  onend: (() => void) | null;
  start(): void; abort(): void;
}
type RecognitionConstructor = new () => Recognition;
type PendingSave = Parameters<typeof saveLesson>[1];

function Teacher() {
  const [lesson, setLesson] = useState<LessonState | null>(null);
  const [session, setSession] = useState<AuthSessionResponse | null>(null);
  const [notice, setNotice] = useState("Cargando tu última lección…");
  const [error, setError] = useState("");
  const [active, setActive] = useState(false);
  const [working, setWorking] = useState(false);
  const [voice, setVoice] = useState<AiVoiceProfile>("feminine");
  const [conflict, setConflict] = useState(false);
  const state = useRef<LessonState | null>(null);
  const auth = useRef<AuthSessionResponse | null>(null);
  const mounted = useRef(false);
  const enabled = useRef(false);
  const busy = useRef(false);
  const generation = useRef(0);
  const needsResume = useRef(true);
  const recognition = useRef<Recognition | null>(null);
  const audio = useRef<HTMLAudioElement | null>(null);
  const audioUrl = useRef<string | null>(null);
  const restart = useRef<ReturnType<typeof setTimeout> | null>(null);
  const debounce = useRef<ReturnType<typeof setTimeout> | null>(null);
  const dirty = useRef(0);
  const saved = useRef(0);
  const failed = useRef(false);
  const saving = useRef<Promise<void> | null>(null);
  const pending = useRef<{ input: PendingSave; change: number } | null>(null);
  const durable = useRef(false);

  function publish(value: LessonState) {
    state.current = value;
    if (mounted.current) setLesson(value);
  }
  function currentPage(): LessonPage {
    const value = state.current!;
    return value.pages.find(p => p.number === value.page)!;
  }
  function releaseAudio() {
    if (audio.current) {
      audio.current.onended = null;
      audio.current.onerror = null;
      audio.current.pause();
      audio.current = null;
    }
    if (audioUrl.current) URL.revokeObjectURL(audioUrl.current);
    audioUrl.current = null;
  }
  function stopVoice() {
    enabled.current = false;
    generation.current += 1;
    if (restart.current) clearTimeout(restart.current);
    const rec = recognition.current;
    recognition.current = null;
    if (rec) { rec.onend = null; rec.onresult = null; rec.onerror = null; rec.abort(); }
    const wasPlaying = audio.current !== null;
    releaseAudio();
    if (wasPlaying) { busy.current = false; if (mounted.current) setWorking(false); }
    if (mounted.current) setActive(false);
  }
  async function initialize() {
    const account = await getOrCreateAuthSession();
    const loaded = await loadLesson(account.sessionToken);
    if (!mounted.current) return;
    auth.current = account;
    setSession(account);
    durable.current = loaded.durable;
    pending.current = null;
    failed.current = false;
    dirty.current = saved.current = 0;
    setConflict(false);
    setError("");
    publish(loaded.lesson);
    needsResume.current = true;
    setNotice(loaded.durable ? "Lección preparada." : "Guardado temporal: el servidor no tiene persistencia activada.");
  }
  useEffect(() => {
    mounted.current = true;
    void initialize().catch(e => { if (mounted.current) setError(e instanceof Error ? e.message : "No se pudo cargar la lección."); });
    const guard = (event: BeforeUnloadEvent) => {
      if (dirty.current !== saved.current) { event.preventDefault(); event.returnValue = ""; }
    };
    const visibility = () => { if (document.hidden) stopVoice(); };
    window.addEventListener("beforeunload", guard);
    document.addEventListener("visibilitychange", visibility);
    return () => {
      mounted.current = false;
      stopVoice();
      if (debounce.current) clearTimeout(debounce.current);
      // Complete an in-flight save or flush the last edits when switching modules.
      if (!failed.current && dirty.current !== saved.current) void flush().catch(() => undefined);
      window.removeEventListener("beforeunload", guard);
      document.removeEventListener("visibilitychange", visibility);
    };
  }, []);

  async function flush(): Promise<void> {
    if (saving.current) { await saving.current; if (dirty.current !== saved.current) return flush(); return; }
    const operation = (async () => {
      while (auth.current && state.current && dirty.current !== saved.current) {
        if (!pending.current) pending.current = {
          change: dirty.current,
          input: { expectedVersion: state.current.version, requestId: crypto.randomUUID(),
            page: { ...currentPage() }, summary: state.current.summary, next: state.current.next }
        };
        const snapshot = pending.current;
        if (mounted.current) setNotice("Guardando…");
        const response = await saveLesson(auth.current.sessionToken, snapshot.input);
        publish({ ...state.current, version: response.lesson.version });
        saved.current = snapshot.change;
        pending.current = null;
        durable.current = response.durable;
      }
      failed.current = false;
      if (mounted.current) setNotice(durable.current ? "Guardado en tu cuenta." : "Guardado temporal en el servidor.");
    })();
    saving.current = operation;
    try { await operation; }
    catch (e) {
      failed.current = true;
      stopVoice();
      if (mounted.current) {
        setError(e instanceof Error ? e.message : "No se pudo guardar. Tu texto sigue en pantalla.");
        setConflict(e instanceof TeacherClientError && e.status === 409);
        setNotice("Cambios pendientes de guardar.");
      }
      throw e;
    } finally { saving.current = null; }
  }
  function changed(value: LessonState) {
    publish(value);
    dirty.current += 1;
    if (debounce.current) clearTimeout(debounce.current);
    if (!failed.current) debounce.current = setTimeout(() => void flush().catch(() => undefined), 600);
  }
  function edit(text: string) {
    if (!state.current || busy.current) return;
    const page = currentPage();
    changed({ ...state.current, pages: state.current.pages.map(p =>
      p.number === page.number ? { ...p, text, correction: "" } : p) });
  }
  async function movePage(direction: number) {
    if (!state.current || busy.current || failed.current) return;
    busy.current = true; setWorking(true);
    // Avoid losing dictated words while a page transition is being saved.
    stopVoice();
    try {
      await flush();
      const value = state.current;
      const target = value.page + direction;
      if (target < 1 || target > 200) return;
      const pages = value.pages.some(p => p.number === target) ? value.pages :
        [...value.pages, { number: target, text: "", correction: "" }];
      changed({ ...value, pages, page: target });
      await flush();
    } catch { /* flush displays the actionable error */ }
    finally { busy.current = false; if (mounted.current) setWorking(false); }
  }
  function listen() {
    if (!enabled.current || busy.current || !mounted.current || recognition.current) return;
    const speechWindow = window as unknown as { SpeechRecognition?: RecognitionConstructor; webkitSpeechRecognition?: RecognitionConstructor };
    const Constructor = speechWindow.SpeechRecognition ?? speechWindow.webkitSpeechRecognition;
    if (!Constructor) {
      stopVoice();
      setError("El dictado no está disponible en este dispositivo. Puedes usar el cuaderno por escrito.");
      return;
    }
    const rec = new Constructor();
    recognition.current = rec;
    rec.lang = "es-ES"; rec.continuous = true; rec.interimResults = false;
    rec.onresult = event => {
      if (!enabled.current || busy.current || recognition.current !== rec) return;
      for (let i = event.resultIndex; i < event.results.length; i += 1) {
        if (!event.results[i].isFinal) continue;
        const transcript = event.results[i][0].transcript;
        if (isAdvanceCommand(transcript)) { void advance(true); break; }
        try { edit(appendDictation(currentPage().text, transcript)); }
        catch (e) { stopVoice(); setError((e as Error).message); break; }
      }
    };
    rec.onerror = event => {
      if (event.error === "no-speech" || event.error === "aborted") return;
      stopVoice();
      setError(event.error === "not-allowed" ? "Permite el micrófono para empezar la sesión." : "Se interrumpió el dictado. Pulsa Iniciar voz para reintentarlo.");
    };
    rec.onend = () => {
      if (recognition.current === rec) recognition.current = null;
      if (enabled.current && !busy.current) restart.current = setTimeout(listen, 250);
    };
    try { rec.start(); setNotice("Escuchando. Di «adelante» por separado cuando termine tu turno."); }
    catch { stopVoice(); setError("No se pudo activar el micrófono."); }
  }
  function startVoice() {
    if (!state.current || busy.current || failed.current) return;
    setError(""); enabled.current = true; setActive(true); listen();
  }
  async function advance(spoken: boolean) {
    if (busy.current || !state.current || !auth.current || failed.current) return;
    busy.current = true; setWorking(true); setError("");
    const turn = generation.current;
    const rec = recognition.current;
    recognition.current = null;
    if (rec) { rec.onend = null; rec.onresult = null; rec.abort(); }
    let playing = false;
    try {
      await flush();
      if (!mounted.current || turn !== generation.current) return;
      setNotice("Preparando la explicación…");
      const page = { ...currentPage() };
      const response = await sendAiChat({
        moduleId: "profesor-voz", sessionId: auth.current.sessionId, sessionToken: auth.current.sessionToken,
        organizationId: getActiveUltraOrganizationId() ?? undefined,
        messages: [{ role: "user", content: teacherPrompt(state.current, page, needsResume.current) }],
        metadata: { locale: "es", platform: "web" },
        userConsent: { privacyPolicyAccepted: true, termsAccepted: true, aiDisclosureAccepted: true,
          safetyPolicyAccepted: true, memoryOptIn: false }
      });
      if (!mounted.current || turn !== generation.current) return;
      const reply = parseTeacherReply(response.message.content);
      changed({ ...state.current, summary: reply.summary, next: reply.next,
        pages: state.current.pages.map(p => p.number === page.number ? { ...p, correction: reply.correction } : p) });
      await flush();
      needsResume.current = false;
      if (!mounted.current || turn !== generation.current) return;
      if (spoken && enabled.current) {
        const blob = await synthesizeAiSpeech({
          text: reply.correction + "\n" + reply.next, sessionToken: auth.current.sessionToken,
          voiceProfile: voice, voiceLanguage: "es"
        });
        if (!mounted.current || turn !== generation.current || !enabled.current) return;
        releaseAudio();
        audioUrl.current = URL.createObjectURL(blob);
        const player = new Audio(audioUrl.current);
        audio.current = player;
        const finish = () => {
          releaseAudio(); busy.current = false;
          if (mounted.current) { setWorking(false); listen(); }
        };
        player.onended = finish;
        player.onerror = () => { setError("La explicación está guardada, pero el audio no pudo reproducirse."); finish(); };
        await player.play();
        playing = true; setNotice("El profesor está hablando.");
      }
    } catch (e) {
      if (mounted.current) setError(e instanceof Error ? e.message : "No se pudo completar este turno.");
    } finally {
      if (!playing) {
        busy.current = false;
        if (mounted.current) { setWorking(false); listen(); }
      }
    }
  }
  function pause() {
    const wasPlaying = audio.current !== null;
    stopVoice();
    if (wasPlaying) { busy.current = false; setWorking(false); }
    setNotice("Sesión de voz detenida.");
  }
  async function reload() {
    if (dirty.current !== saved.current &&
      !window.confirm("Hay cambios sin guardar. ¿Descartarlos y cargar la lección de tu cuenta?")) return;
    pause();
    if (debounce.current) clearTimeout(debounce.current);
    try { if (saving.current) await saving.current.catch(() => undefined); await initialize(); }
    catch (e) { setError((e as Error).message); }
  }
  const page = lesson?.pages.find(p => p.number === lesson.page);
  return <section className="teacher-shell" aria-labelledby="teacher-title">
    <header><p className="eyebrow">OMNISYNIA</p><h2 id="teacher-title">Profesor por voz</h2>
      <p>Habla con calma. Di «adelante» por separado para que el profesor responda.</p></header>
    {lesson && <aside className="teacher-resume" aria-label="Retomar la lección">
      <strong>{lesson.version ? "Donde lo dejamos" : "Tu primera lección"}</strong>
      {lesson.summary && <p>{lesson.summary}</p>}
      <p>Sigue aquí: {lesson.next}</p><small>Página {lesson.page}</small>
    </aside>}
    <div className="teacher-controls">
      <button className="secondary-button" disabled={!lesson || (!active && (working || failed.current))}
        onClick={active ? pause : startVoice}>{active ? "Detener voz" : "Iniciar voz"}</button>
      <select aria-label="Voz del profesor" disabled={active || working} value={voice}
        onChange={e => setVoice(e.target.value as AiVoiceProfile)}>
        <option value="feminine">Voz femenina</option><option value="masculine">Voz masculina</option>
      </select>
    </div>
    <p role="status">{notice}</p>
    {session?.user.mode === "anonymous" && <p>Entra en tu cuenta para retomar la lección desde otro dispositivo.</p>}
    {error && <div role="alert"><p>{error}</p>
      {failed.current && !conflict && <button className="secondary-button" disabled={working}
        onClick={() => void flush().then(() => setError("")).catch(() => undefined)}>Reintentar guardado</button>}
      <button className="secondary-button" disabled={working} onClick={() => void reload()}>Recargar lección</button>
    </div>}
    {page && <div className="teacher-notebook">
      <WritingTextarea label={<>Tu texto · Página {page.number}</>} key={page.number} rows={14} maxLength={4000} value={page.text} disabled={working}
          onValueChange={text => edit(text)} onBlur={() => {
            if (!failed.current) void flush().catch(() => undefined);
          }} />
      <article aria-label="Correcciones del profesor"><h3>Correcciones y explicación</h3>
        <p>{page.correction || "Aquí aparecerán las correcciones al dar paso al profesor."}</p></article>
    </div>}
    {lesson && <div className="teacher-controls">
      <button className="secondary-button" disabled={working || failed.current || lesson.page <= 1}
        onClick={() => void movePage(-1)}>Página anterior</button>
      <span>{lesson.page} / {lesson.pages.length}</span>
      <button className="secondary-button" disabled={working || failed.current || lesson.page >= 200}
        onClick={() => void movePage(1)}>Página siguiente</button>
      {!active && <button className="primary-button" disabled={working || failed.current}
        onClick={() => void advance(false)}>Adelante por escrito</button>}
    </div>}
  </section>;
}

export const teacherClientModule: OmnisyniaClientModule = {
  id: "profesor-voz", displayName: "Profesor por voz",
  description: "Lecciones con voz por turnos y cuaderno personal.",
  tier: "core", status: "available", Component: Teacher
};
